/**
 * What the top-up dialog does (PV10, the top-up dialog part 1, 2026-10-13): the amount, the card, the charge, the
 * credit landing, the promo code and the auto top-up offer.
 *
 * Lifted out of src/components/wallet/TopUpModal.tsx so the new design's top-up dialog, which may not import today's
 * styled components, draws the same dialog from the same behaviour. Today's dialog calls this hook and only draws. It
 * must be called inside Stripe's <Elements>, as the dialog always was (TopUpModalProvider mounts it there).
 *
 * Nothing here changed in the move: the money math, the one idempotency id per attempt, the default card preselected,
 * the success seen on the live balance with its 12 s fallback, auto top-up enrolled only by a completed card payment
 * and never in org mode, the org-blocked screen failing open while the permission is read, Escape and the backdrop
 * locked only while the charge is in flight, and the page held still while the dialog is open.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { inCurrentTree } from '../design/previewPath';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { stripeApi } from '../services/stripeClient';
import { useMaybeWalletSummary } from '../context/WalletSummaryContext';
import { useMaybeOLVRate } from '../context/OLVRateContext';
import { useAutoTopUp } from './useAutoTopUp';
import { useUsageVisibility } from './useUsageVisibility';
import {
  listOrgSharedPaymentMethods,
  SharedPaymentMethod,
} from '../services/paymentMethodSharing';
import { AUTO_TOP_UP_DEFAULTS } from '../types/autoTopUp';
import { useAccountBurnRate } from './useAccountBurnRate';
import { suggestTopUpAmounts } from '../utils/burnRate';
import { pickDefaultChip, seedAutoTopUpFields } from '../utils/topUpDecisions';
import { useWorkspaceTrialStatus, type TrialStatus } from './useTrialStatus';
import { useBodyScrollLock } from './useBodyScrollLock';
import { usdToWu, wuToUsd } from '../config/workUnits';
import { redeemAccessCode } from '../services/accessCodeClient';

// ============================================================================
// Constants
// ============================================================================

/**
 * Suggested amounts — owner-directed (2026-08-14): meaningful pr0 quantities,
 * much larger than the retired $10–$100 chips. USD mode defaults to $500
 * (matches the auto top-up default amount).
 */
const USD_PRESETS = [100, 250, 500, 1000];
const DEFAULT_USD_PRESET = 500;
/** Work-unit mode chips (≈ $109 / $218 / $545 / $1,090 at the Standard Rate). */
const WU_PRESETS = [50, 100, 250, 500];
const DEFAULT_WU_PRESET = 250;

export const MIN_TOP_UP_USD = 2;
const FALLBACK_RATE_USD = 0.001;
/** If the realtime balance hasn't flipped this long after a confirmed credit, force success. */
export const CREDIT_FALLBACK_MS = 12000;
/** Auto-dismiss the success state this long after it appears. */
export const SUCCESS_AUTOCLOSE_MS = 2600;

/**
 * Idempotency attempt id (2026-08-18 audit): the server folds this into the
 * Stripe idempotency key, so a double-click or retried network call can
 * never charge twice. One id per ATTEMPT — kept across error retries,
 * re-minted when the amount/card changes or after a success.
 */
const newAttemptId = stripeApi.newPaymentAttemptId;

export type SavedCard = { id: string; brand?: string; last4?: string; exp_month?: number; exp_year?: number; is_default?: boolean };
export type TopUpStatus = 'idle' | 'processing' | 'crediting' | 'success' | 'error';
export type TopUpMode = 'usd' | 'wu';

export function feeFor(usd: number): number {
  return Math.round((0.3 + usd * 0.029) * 100) / 100;
}

/** The trial facts the dialog's note draws. */
export type TopUpTrialView = Pick<TrialStatus, 'isTrialUser' | 'isActive' | 'isExpiringSoon' | 'daysRemaining' | 'endsOn'>;

/** Everything a top-up dialog draws, and what each of its controls does. */
export interface TopUpDialogView {
  /** Which screen: the success screen wins, then the org-blocked one, then the trial-ended one, else the form. */
  status: TopUpStatus;
  orgBlocked: boolean;
  trialBlocked: boolean;
  trial: TopUpTrialView;

  mode: TopUpMode;
  switchMode: (next: TopUpMode) => void;
  /** The chips for this mode: the organization's own pace when it is known, else the flat ladder. */
  presets: number[];
  selectedPreset: number | 'custom';
  choosePreset: (amount: number) => void;
  /** amount -> "about a week at your pace", when the pace is known. */
  runwayFor: Record<number, string>;
  customAmount: string;
  typeCustomAmount: (value: string) => void;
  focusCustomAmount: () => void;

  effectiveAmountUsd: number;
  fee: number;
  total: number;
  /** Display-only pr0 at the published Standard Rate. */
  estWu: number;
  belowMin: boolean;
  isBusy: boolean;

  loadingMethods: boolean;
  savedCards: SavedCard[];
  /** Org mode: the cards other members shared with this organization. */
  orgSharedCards: SharedPaymentMethod[];
  selectedPmId: string;
  selectedSharedPmId: string;
  useNewCard: boolean;
  chooseSharedCard: (id: string) => void;
  chooseSavedCard: (id: string) => void;
  chooseNewCard: () => void;
  /** The workspace wallet an org top-up credits; null when the money lands in the person's own wallet. */
  orgTopUpTarget: string | null;

  /** The signed-in uid auto top-up keys on; the offer is disabled without one. */
  uid: string | null;
  autoWanted: boolean;
  handleAutoToggle: (next: boolean) => void;
  autoEnabled: boolean;
  autoLoading: boolean;
  autoError: string | null;
  atuThreshold: string;
  setAtuThreshold: (value: string) => void;
  atuAmount: string;
  setAtuAmount: (value: string) => void;
  /** The figures the auto top-up line names: what is typed, else the defaults. */
  autoThresholdUsd: number;
  autoAmountUsd: number;
  commitThreshold: () => void;
  commitAmount: () => void;

  promoOpen: boolean;
  openPromo: () => void;
  promoCode: string;
  typePromoCode: (value: string) => void;
  promoBusy: boolean;
  promoError: string | null;
  handleRedeemPromo: () => Promise<void>;
  /** pr0 credited by a redeemed code — drives the success copy. */
  codeCreditedWu: number | null;
  /** Set when success was forced by the crediting fallback (payment confirmed, credit still landing). */
  successNote: string | null;
  errorMsg: string | null;

  handleSubmit: () => Promise<void>;
  onClose: () => void;
  /** The backdrop's press: closes, except while the charge is in flight. */
  onBackdropMouseDown: (e: React.MouseEvent) => void;
  /** The trial-ended screen's action: the subscription page. */
  activateAccount: () => void;
  /** Where the dialog is portaled: the page's body. */
  portalTarget: HTMLElement | null;
}

export function useTopUpDialog({ onClose }: { onClose: () => void }): TopUpDialogView {
  const stripe = useStripe();
  const elements = useElements();
  const wallet = useMaybeWalletSummary();
  const olvRate = useMaybeOLVRate();
  const trialStatus = useWorkspaceTrialStatus();
  // Expired trial accounts cannot add tokens or credits — they hire their
  // Prest0n on the subscription page instead (self-serve since task #59).
  const trialBlocked = trialStatus.isTrialUser && trialStatus.isExpired;

  // PERSONAL balance on purpose (2026-07-13 account-wallet-display change):
  // the top-up credits the signed-in user's OWN wallet, and the crediting
  // success detector below watches this number rise — so it must track the
  // wallet the money actually lands in, never the (possibly account-scope)
  // displayed summary. Fallback keeps older provider shapes working in tests.
  //
  // Task #80 — ORG top-up: when the user is acting in a workspace whose
  // paying wallet is NOT their own (displayScope 'account'), the top-up
  // charges THEIR card but credits the ORG's wallet (billing.manage gated
  // below; the server enforces it again). In that mode the success detector
  // watches the DISPLAYED (account) balance — the org mirror is what rises.
  const usageVisibility = useUsageVisibility();
  const orgTopUpTarget =
    wallet?.displayScope === 'account' && wallet.displayAccountId ? wallet.displayAccountId : null;
  // `ready` gate (2026-08-18 audit): while the permission read is still in
  // flight canManageBilling is false, which flashed the blocked screen at
  // every org admin for a beat. Fail OPEN while loading (house contract —
  // the server re-gates every write anyway).
  const orgBlocked = orgTopUpTarget !== null && usageVisibility.ready && !usageVisibility.canManageBilling;
  const olvBalance = orgTopUpTarget
    ? wallet?.summary.olvBalance ?? 0
    : wallet?.personalSummary?.olvBalance ?? wallet?.summary.olvBalance ?? 0;
  const rate = olvRate?.getEffectiveRate() ?? FALLBACK_RATE_USD;

  // Firebase auth uid (Firestore autoTopUp rules key on the real auth uid).
  const [uid, setUid] = useState<string | null>(() => getAuth().currentUser?.uid ?? null);
  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (u) => setUid(u && !u.isAnonymous ? u.uid : null));
    return unsub;
  }, []);

  const autoTopUp = useAutoTopUp(uid);
  // This organization's own spending pace, for the personalised ladder below.
  const { burn } = useAccountBurnRate(uid);

  // Amount entry: by-USD / by-work-units modes, each with suggested chips.
  const [mode, setMode] = useState<TopUpMode>('usd');
  const [selectedPreset, setSelectedPreset] = useState<number | 'custom'>(DEFAULT_USD_PRESET);
  const [customAmount, setCustomAmount] = useState('');

  const switchMode = useCallback((next: TopUpMode) => {
    setMode(next);
    setSelectedPreset(next === 'usd' ? DEFAULT_USD_PRESET : DEFAULT_WU_PRESET);
    setCustomAmount('');
  }, []);

  /** The USD amount the user is buying (WU-mode entries convert at the Standard Rate). */
  const effectiveAmountUsd = useMemo(() => {
    const raw = selectedPreset === 'custom' ? parseFloat(customAmount) || 0 : selectedPreset;
    if (mode === 'wu') return Math.round(wuToUsd(raw) * 100) / 100;
    return raw;
  }, [mode, selectedPreset, customAmount]);

  // Payment method selection
  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [selectedPmId, setSelectedPmId] = useState<string>('');
  const [useNewCard, setUseNewCard] = useState(false);
  // Task #80: cards other members SHARED with this organization — offered
  // only in org mode, only when the server grants the view permission (the
  // listing helper returns [] otherwise), and paying with one is re-gated
  // server-side on billing.payment-methods.use.
  const [orgSharedCards, setOrgSharedCards] = useState<SharedPaymentMethod[]>([]);
  const [selectedSharedPmId, setSelectedSharedPmId] = useState<string>('');

  // Purchase lifecycle
  const [status, setStatus] = useState<TopUpStatus>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Variant copy when success was forced by the crediting-fallback timer
  // (payment confirmed, credit still propagating).
  const [successNote, setSuccessNote] = useState<string | null>(null);
  // True only after a CARD purchase reaches 'crediting' — the auto top-up
  // enrollment effect requires it, so a promo-code redemption (which lands
  // in the same success state) can never enroll auto top-up.
  const cardPurchaseRef = useRef(false);

  // One idempotency attempt id per payment attempt (see newAttemptId).
  const attemptIdRef = useRef(newAttemptId());
  useEffect(() => {
    attemptIdRef.current = newAttemptId();
  }, [effectiveAmountUsd, selectedPmId, selectedSharedPmId, useNewCard]);
  useEffect(() => {
    if (status === 'success') attemptIdRef.current = newAttemptId();
  }, [status]);

  // Promo code (PR0-… wallet-credit codes). Redeeming is an alternative way
  // to add pr0 without a card, and lands in the SAME success state.
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  /** pr0 credited by a redeemed code — drives the success copy. */
  const [codeCreditedWu, setCodeCreditedWu] = useState<number | null>(null);
  const baselineOlvRef = useRef<number | null>(null);
  const timersRef = useRef<number[]>([]);

  // Auto top-up UI. `autoWanted` is the checkbox: it defaults ON for users
  // who have never made an auto top-up choice, but enrollment only happens
  // when the user CONFIRMS by completing a top-up (see the success effect).
  // A stored choice (on OR off) always seeds the checkbox instead.
  const [autoWanted, setAutoWanted] = useState(false);
  const autoSeededRef = useRef(false);
  const [atuThreshold, setAtuThreshold] = useState(String(AUTO_TOP_UP_DEFAULTS.thresholdUsd));
  const [atuAmount, setAtuAmount] = useState(String(AUTO_TOP_UP_DEFAULTS.topUpAmountUsd));

  const registerTimer = useCallback((id: number) => {
    timersRef.current.push(id);
  }, []);

  useEffect(() => {
    const timers = timersRef;
    return () => {
      timers.current.forEach((id) => window.clearTimeout(id));
      timers.current = [];
    };
  }, []);

  // Seed the auto top-up inputs. A STORED choice always wins — this must never
  // overwrite a figure the user set. Only a never-configured account gets the
  // usage-derived suggestion (~6 days of its own pace, with a threshold that
  // buys real runway) instead of the flat $25/$500 that charged Fernando.
  useEffect(() => {
    const seed = seedAutoTopUpFields({
      hasStoredSettings: autoTopUp.hasStoredSettings,
      stored: autoTopUp.settings,
      burn,
      fallback: AUTO_TOP_UP_DEFAULTS,
    });
    setAtuThreshold(String(seed.thresholdUsd));
    setAtuAmount(String(seed.topUpAmountUsd));
  }, [
    autoTopUp.hasStoredSettings,
    autoTopUp.settings.thresholdUsd,
    autoTopUp.settings.topUpAmountUsd,
    burn,
  ]);

  // Pre-select the "about a week" chip once the pace is known — the middle of
  // the ladder, not the biggest number on it. Never overrides a user's pick:
  // it runs once, and only while the untouched flat default is still selected.
  const presetSeededRef = useRef(false);
  useEffect(() => {
    if (presetSeededRef.current || !burn?.confident || mode !== 'usd') return;
    const ladder = suggestTopUpAmounts(burn);
    if (ladder.length === 0) return;
    presetSeededRef.current = true;
    if (selectedPreset !== DEFAULT_USD_PRESET) return; // the user already chose
    const chip = pickDefaultChip(ladder);
    if (chip) setSelectedPreset(chip.usd);
  }, [burn, mode, selectedPreset]);

  // Seed the checkbox from the user's OWN stored choice, and nothing else.
  //
  // INCIDENT 2026-09-10. This used to read
  //   settings.enabled || !hasStoredSettings
  // — pre-ticked for anyone who had never chosen. Combined with an enrolment
  // effect that fired on a success screen which auto-dismisses after 2.6s, a
  // customer 32 seconds past "work paused — wallet is empty" was signed up for
  // $500 recurring off-session charges without ever making a decision. Four
  // weeks later his card was charged and he found out from his bank.
  //
  // Owner ruling (Miles, 2026-09-11): opt-in, offered after the first real
  // payment — "nobody running on free credit is ever enrolled". The offer is
  // still shown here, because completing a card payment IS that first real
  // payment; it simply starts unticked, and the server refuses to arm an
  // account that has never knowingly paid (functions/src/billing/knowinglyPaid.ts).
  useEffect(() => {
    if (autoSeededRef.current || autoTopUp.isLoading) return;
    autoSeededRef.current = true;
    setAutoWanted(autoTopUp.settings.enabled);
  }, [autoTopUp.isLoading, autoTopUp.settings.enabled]);

  // Org mode: load the cards shared with this workspace (empty without the
  // view permission — the section simply doesn't render).
  useEffect(() => {
    let alive = true;
    if (!orgTopUpTarget) {
      setOrgSharedCards([]);
      setSelectedSharedPmId('');
      return;
    }
    void listOrgSharedPaymentMethods(orgTopUpTarget).then((methods) => {
      if (alive) setOrgSharedCards(methods);
    });
    return () => {
      alive = false;
    };
  }, [orgTopUpTarget]);

  // Fetch saved cards on mount to offer one-click pay.
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingMethods(true);
      try {
        const res = await stripeApi.listMethods();
        const methods = ((res as { methods?: SavedCard[] })?.methods || []) as SavedCard[];
        if (!alive) return;
        setSavedCards(methods);
        if (methods.length > 0) {
          // Preselect the customer's DEFAULT card (2026-08-18 audit) — the
          // server marks it; first-in-list was an arbitrary choice.
          const preferred = methods.find((m) => m.is_default) ?? methods[0];
          setSelectedPmId(preferred.id);
          setUseNewCard(false);
        } else {
          setUseNewCard(true);
        }
      } catch {
        if (alive) setUseNewCard(true);
      } finally {
        if (alive) setLoadingMethods(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Escape-to-close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && status !== 'processing') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [status, onClose]);

  // Lock body scroll while open — shared reference-counted lock (2026-08-12
  // scrolling round) so overlapping overlays can't restore each other's
  // snapshots out of order.
  useBodyScrollLock(true);

  // Success WITHOUT navigation: watch the realtime wallet balance rise above
  // the snapshot we took at submit time.
  useEffect(() => {
    if (status !== 'crediting') return;
    if (baselineOlvRef.current == null) return;
    if (olvBalance > baselineOlvRef.current + 1e-6) {
      setSuccessNote(null);
      setStatus('success');
    }
  }, [status, olvBalance]);

  // Crediting fallback: ALWAYS armed on entering 'crediting'. The payment is
  // confirmed at this point — if the realtime balance hasn't flipped us to
  // success in CREDIT_FALLBACK_MS, force success with honest variant copy
  // instead of holding the user hostage on a spinner.
  useEffect(() => {
    if (status !== 'crediting') return;
    const id = window.setTimeout(() => {
      setSuccessNote('payment received — your credit lands shortly.');
      setStatus('success');
    }, CREDIT_FALLBACK_MS);
    registerTimer(id);
    return () => window.clearTimeout(id);
  }, [status, registerTimer]);

  // Auto-dismiss the success state.
  useEffect(() => {
    if (status !== 'success') return;
    const id = window.setTimeout(() => onClose(), SUCCESS_AUTOCLOSE_MS);
    registerTimer(id);
    return () => window.clearTimeout(id);
  }, [status, onClose, registerTimer]);

  // Auto top-up ENROLLMENT: the completed top-up is the user's confirmation.
  // Runs once per modal lifetime, only on success, only when the box is
  // checked and auto top-up isn't already enrolled.
  const autoEnrolledRef = useRef(false);
  useEffect(() => {
    if (status !== 'success' || autoEnrolledRef.current) return;
    autoEnrolledRef.current = true;
    // Never enroll auto top-up off an ORG top-up — it's a personal
    // off-session flow and would silently refill the admin's own wallet.
    if (cardPurchaseRef.current && autoWanted && !autoTopUp.settings.enabled && uid && !orgTopUpTarget) {
      const approvedAmount = parseFloat(atuAmount) || AUTO_TOP_UP_DEFAULTS.topUpAmountUsd;
      autoTopUp
        .enable({
          thresholdUsd: parseFloat(atuThreshold) || AUTO_TOP_UP_DEFAULTS.thresholdUsd,
          topUpAmountUsd: approvedAmount,
          // The figure the user actually saw and agreed to. Suggested amounts
          // track usage, so the server may lower a future charge freely but
          // must never raise it past this without asking (autoTopUp.ts).
          confirmedAmountUsd: approvedAmount,
          paymentMethodId: !useNewCard && selectedPmId ? selectedPmId : undefined,
          // The digits are already on screen in the card picker. Recording them
          // here is what lets the pre-charge warning NAME the card, which is
          // what makes a charge recognisable rather than a surprise; the server
          // alternative is a Stripe round trip from a Firestore trigger.
          cardLast4:
            (!useNewCard && selectedPmId
              ? savedCards.find((c) => c.id === selectedPmId)?.last4
              : undefined) || undefined,
        })
        .catch(() => {
          /* surfaced via autoTopUp.error on next open; never blocks the success state */
        });
    }
  }, [status, autoWanted, autoTopUp, uid, atuThreshold, atuAmount, useNewCard, selectedPmId, orgTopUpTarget]);

  const fee = feeFor(effectiveAmountUsd);
  const total = Math.round((effectiveAmountUsd + fee) * 100) / 100;
  // Display-only WU estimate at the published Standard Rate ($2.18 / WU).
  // The payment flow below still uses the live rate — DO NOT reuse this there.
  const estWu = usdToWu(effectiveAmountUsd);
  const isBusy = status === 'processing' || status === 'crediting';
  const belowMin = effectiveAmountUsd < MIN_TOP_UP_USD;

  const handleSubmit = useCallback(async () => {
    setErrorMsg(null);
    const usd = effectiveAmountUsd;
    if (!usd || usd < MIN_TOP_UP_USD) {
      setErrorMsg(`minimum top-up is $${MIN_TOP_UP_USD.toFixed(2)}.`);
      return;
    }
    if (!stripe || !elements) {
      setErrorMsg('payments are still loading — please try again in a moment.');
      return;
    }
    if (!useNewCard && !selectedPmId && !selectedSharedPmId) {
      setErrorMsg('please choose a saved card or add a new one.');
      return;
    }

    const usdFee = feeFor(usd);
    const usdTotal = Math.round((usd + usdFee) * 100) / 100;
    const tokens = rate > 0 ? Math.max(1, Math.round(usd / rate)) : Math.max(1, Math.round(usd * 1000));

    baselineOlvRef.current = olvBalance;
    setSuccessNote(null);
    setStatus('processing');

    try {
      const usingSharedCard = !useNewCard && !!selectedSharedPmId && !!orgTopUpTarget;
      const { clientSecret, error } = await stripeApi.paymentIntent({
        operation: 'buy_tokens',
        tokens,
        amountUsd: usdTotal,
        baseFee: usdFee,
        paymentMethodId: useNewCard || usingSharedCard ? undefined : selectedPmId,
        // Org top-up: the server gates on billing.manage and stamps the
        // credit destination into the intent (the org's paying wallet).
        ...(orgTopUpTarget ? { accountId: orgTopUpTarget } : {}),
        // Shared org card: the server re-gates on the use permission and
        // creates the intent on the card owner's customer.
        ...(usingSharedCard ? { sharedPaymentMethodId: selectedSharedPmId } : {}),
        attemptId: attemptIdRef.current,
      });
      if (error || !clientSecret) {
        setStatus('error');
        setErrorMsg(error || 'could not start the payment. please try again.');
        return;
      }

      const confirmResult = useNewCard
        ? await stripe.confirmCardPayment(clientSecret, {
            payment_method: { card: elements.getElement(CardElement)! },
          })
        : await stripe.confirmCardPayment(clientSecret, {
            payment_method: usingSharedCard ? selectedSharedPmId : selectedPmId,
          });

      if (confirmResult.error) {
        setStatus('error');
        setErrorMsg(confirmResult.error.message || 'payment failed. please try again.');
        return;
      }

      // Move to "crediting": the WalletSummaryContext balance listener will
      // flip us to success once Firestore reflects the credit.
      cardPurchaseRef.current = true;
      setStatus('crediting');

      // The crediting-fallback timer is armed by the status-transition effect
      // below — ALWAYS, not just when this call succeeds (2026-08-18 audit:
      // a confirm throw or a {credited:false} result used to leave no timer,
      // stranding the modal on "crediting…" forever).
      const piId = confirmResult.paymentIntent?.id;
      if (piId) {
        try {
          await stripeApi.confirmAndCreditPayment(piId);
        } catch {
          /* webhook backup credits; the balance listener still resolves success */
        }
      }
    } catch (e) {
      setStatus('error');
      setErrorMsg((e as Error)?.message || 'something went wrong. please try again.');
    }
  }, [effectiveAmountUsd, stripe, elements, useNewCard, selectedPmId, selectedSharedPmId, rate, olvBalance, registerTimer, orgTopUpTarget]);

  /**
   * Redeem a promo code. The server credits the account's PAYING wallet and
   * returns the applied amount; we land in the same success state a card
   * purchase reaches, so the wallet balance the user is looking at updates
   * without a reload.
   */
  const handleRedeemPromo = useCallback(async () => {
    if (!promoCode.trim() || promoBusy) return;
    setPromoError(null);
    setPromoBusy(true);
    try {
      const result = await redeemAccessCode(promoCode);
      if (!result.success) {
        setPromoError(result.message);
        return;
      }
      const cents = typeof result.creditAmountCents === 'number' ? result.creditAmountCents : 0;
      // Display-derived pr0, same convention as every other surface:
      // displayed pr0 = credited USD ÷ the published Standard Rate.
      setCodeCreditedWu(cents > 0 ? usdToWu(cents / 100) : null);
      setPromoCode('');
      setStatus('success');
    } catch {
      setPromoError('we could not apply that code — please try again.');
    } finally {
      setPromoBusy(false);
    }
  }, [promoCode, promoBusy]);

  // Unchecking while ENROLLED turns auto top-up off immediately (turning it
  // off must never require a purchase). Checking is only ever a pending
  // intent confirmed by a completed top-up.
  const handleAutoToggle = useCallback(
    (next: boolean) => {
      setAutoWanted(next);
      if (!next && autoTopUp.settings.enabled) {
        autoTopUp.disable().catch(() => {
          /* surfaced via autoTopUp.error below */
        });
      }
    },
    [autoTopUp],
  );

  const persistAutoField = useCallback(
    (patch: { thresholdUsd?: number; topUpAmountUsd?: number }) => {
      if (!autoTopUp.settings.enabled) return; // only persist while enrolled
      // Editing the amount IS re-approving it — keep the ceiling in step, or
      // the server would hold every future charge at the older, lower figure.
      const withCeiling =
        typeof patch.topUpAmountUsd === 'number'
          ? { ...patch, confirmedAmountUsd: patch.topUpAmountUsd }
          : patch;
      autoTopUp.update(withCeiling).catch(() => {
        /* surfaced via autoTopUp.error */
      });
    },
    [autoTopUp],
  );

  // PERSONALISED LADDER (Miles, 2026-09-11). The chips are this organization's
  // own pace — roughly a few days / a week / two weeks / a month of it —
  // instead of a flat $100/$250/$500/$1,000 with $500 pre-selected. Each one
  // is labelled with how long it lasts, which is what makes the number mean
  // something before anyone clicks it.
  //
  // Falls back to the flat ladder, UNLABELLED, when there is too little
  // history: inventing a pace would be worse than showing round numbers.
  const suggestions = useMemo(
    () => (burn ? suggestTopUpAmounts(burn) : null),
    [burn],
  );
  const usdPresets = useMemo(
    () => (suggestions && burn?.confident ? suggestions.map((s) => s.usd) : USD_PRESETS),
    [suggestions, burn],
  );
  /** amount -> "about a week at your pace", when we know the pace. */
  const runwayFor = useMemo(() => {
    const map: Record<number, string> = {};
    if (suggestions && burn?.confident) for (const s of suggestions) map[s.usd] = s.subLabel;
    return map;
  }, [suggestions, burn]);

  const presets = mode === 'usd' ? usdPresets : WU_PRESETS;

  const autoThresholdUsd = parseFloat(atuThreshold) || AUTO_TOP_UP_DEFAULTS.thresholdUsd;
  const autoAmountUsd = parseFloat(atuAmount) || AUTO_TOP_UP_DEFAULTS.topUpAmountUsd;

  return {
    status,
    orgBlocked,
    trialBlocked,
    trial: {
      isTrialUser: trialStatus.isTrialUser,
      isActive: trialStatus.isActive,
      isExpiringSoon: trialStatus.isExpiringSoon,
      daysRemaining: trialStatus.daysRemaining,
      endsOn: trialStatus.endsOn,
    },
    mode,
    switchMode,
    presets,
    selectedPreset,
    choosePreset: (amount) => setSelectedPreset(amount),
    runwayFor,
    customAmount,
    typeCustomAmount: (value) => {
      setSelectedPreset('custom');
      setCustomAmount(value);
    },
    focusCustomAmount: () => setSelectedPreset('custom'),
    effectiveAmountUsd,
    fee,
    total,
    estWu,
    belowMin,
    isBusy,
    loadingMethods,
    savedCards,
    orgSharedCards,
    selectedPmId,
    selectedSharedPmId,
    useNewCard,
    chooseSharedCard: (id) => {
      setUseNewCard(false);
      setSelectedPmId('');
      setSelectedSharedPmId(id);
    },
    chooseSavedCard: (id) => {
      setUseNewCard(false);
      setSelectedSharedPmId('');
      setSelectedPmId(id);
    },
    chooseNewCard: () => setUseNewCard(true),
    orgTopUpTarget,
    uid,
    autoWanted,
    handleAutoToggle,
    autoEnabled: autoTopUp.settings.enabled,
    autoLoading: autoTopUp.isLoading,
    autoError: autoTopUp.error,
    atuThreshold,
    setAtuThreshold,
    atuAmount,
    setAtuAmount,
    autoThresholdUsd,
    autoAmountUsd,
    commitThreshold: () => persistAutoField({ thresholdUsd: parseFloat(atuThreshold) || AUTO_TOP_UP_DEFAULTS.thresholdUsd }),
    commitAmount: () => persistAutoField({ topUpAmountUsd: parseFloat(atuAmount) || AUTO_TOP_UP_DEFAULTS.topUpAmountUsd }),
    promoOpen,
    openPromo: () => setPromoOpen(true),
    promoCode,
    typePromoCode: (value) => {
      setPromoCode(value);
      if (promoError) setPromoError(null);
    },
    promoBusy,
    promoError,
    handleRedeemPromo,
    codeCreditedWu,
    successNote,
    errorMsg,
    handleSubmit,
    onClose,
    onBackdropMouseDown: (e) => {
      // Backdrop click closes (but not while the CHARGE is in flight —
      // 'crediting' may close: payment done, the credit lands regardless).
      if (e.target === e.currentTarget && status !== 'processing') onClose();
    },
    activateAccount: () => {
      window.location.href = inCurrentTree('/subscription');
    },
    portalTarget: typeof document === 'undefined' ? null : document.body,
  };
}
